import React, { FC, useState } from 'react';
import { FormSubmitHandler, isDirty, SubmissionError } from 'redux-form';
import { PublicJobCreateAlertResponseData, PublicJobSubscriptionFormValues } from './types';
import { usePublicJobSubscriptionSubmitMutation } from './hooks';
import {
	CONFIRMATION_TEMPLATE_TYPE_CODE,
	PUBLIC_JOB_SUBSCRIPTION_FORM_NAME,
	SavedFilterType,
	SubscriberTypeId,
} from './const';
import { PublicJobSubscriptionForm } from './PublicJobSubscriptionForm';
import { isNullOrUndefined, isUndefined } from '../../../../predicates/type';
import { FlexGrid } from '../../../../components/FlexGrid';
import { gray } from '../../../../components/Color';
import { Typography } from '../../../../components/Typography';
import { isBadOrEmptyString } from '../../../../predicates/string';
import { codes } from '../../../../data/translations';
import { useTranslationAPI } from '../../../translations';
import { CustomObject } from '../../../../types';
import { catchErrorResponse } from '../../../app';
import { PublicJobSubscriptionConfirmation } from './PublicJobSubscriptionConfirmation';
import { jobModuleCodes } from '../../../../data/translations/jobModuleCodes';
import { PublicJobSubscriptionTitle } from './PublicJobSubscriptionTitle';
import { DefaultRootState, useSelector } from 'react-redux';
import { useReCaptchaV3 } from '@packages/reCaptcha/useReCaptchaV3';

const createAlertRequiredParams = {
	savedFilter: {
		savedFilterTypeId: SavedFilterType.PublicJob,
	},
	subscriberTypeId: SubscriberTypeId.PublicJob,
	confirmationTemplateTypeCode: CONFIRMATION_TEMPLATE_TYPE_CODE,
};

export type PublicJobSubscriptionProps = {
	filterValues?: CustomObject;
	recaptchaPublicKey?: string | null;
};

export const PublicJobSubscription: FC<React.PropsWithChildren<PublicJobSubscriptionProps>> = ({
	filterValues,
	recaptchaPublicKey,
}) => {
	const { translate } = useTranslationAPI();
	const [subscriptionSubmitMutation, { isLoading }] = usePublicJobSubscriptionSubmitMutation();
	const [messages, setMessages] = useState<PublicJobCreateAlertResponseData | undefined>();

	const isDirtyForm = useSelector<DefaultRootState, boolean>(isDirty(PUBLIC_JOB_SUBSCRIPTION_FORM_NAME));

	const [isReCaptchaInitialized, setIsReCaptchaInitialized] = useState(false);
	const isReCaptchaRequired = !isNullOrUndefined(recaptchaPublicKey) && isReCaptchaInitialized;

	const execute = useReCaptchaV3(isReCaptchaRequired);

	const handleFormSubmit: FormSubmitHandler<PublicJobSubscriptionFormValues> = async (values) => {
		if (isBadOrEmptyString(values.email)) {
			throw new SubmissionError({ email: translate(codes.required) });
		}

		const recaptchaResponse = await execute();

		return subscriptionSubmitMutation({
			...createAlertRequiredParams,
			...filterValues,
			...values,
			recaptchaResponse,
		})
			.then(setMessages)
			.catch(catchErrorResponse);
	};

	const handleFormChange = (values: PublicJobSubscriptionFormValues) => {
		const isEmailValid = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email);

		if (isDirtyForm && isEmailValid && !isReCaptchaInitialized) {
			setIsReCaptchaInitialized(true);
		}
	};

	if (!isUndefined(messages)) {
		return <PublicJobSubscriptionConfirmation {...messages} />;
	}

	return (
		<FlexGrid
			column
			gap={'tiny'}
		>
			<PublicJobSubscriptionTitle isLoading={isLoading} />
			<PublicJobSubscriptionForm
				onSubmit={handleFormSubmit}
				onChange={handleFormChange}
			/>
			<Typography
				color={gray[5]}
				fontSize='12px'
				align='center'
			>
				{translate(jobModuleCodes.form1SubscriptionTip)}
			</Typography>
		</FlexGrid>
	);
};
